// ---- Interactions (client) ----
// Prop interactions layered on the focus system — the third sibling of
// combat.ts (fight) and talk.ts (converse). A deco is INTERACTABLE iff its spec
// has an `interact` block (content/decos/<type>.json); clicking one gives it a
// light focus (ring + banner + action bar, no camera lock — see focus.ts), and
// action-bar slot 1 offers the block's verb (Open / Shake / Pick / Read),
// triggered by clicking the slot or pressing F (E already orbits the camera).
//
// The SERVER is authoritative for every state-mutating kind (index.ts
// `interact` handler): the client sends the intent, the server validates range,
// rolls the tree's drop chance, spawns loot/pinecones as ordinary world items,
// and owns opened-chest / picked-flower state. Results come back as:
//   • `props`      — full opened/hidden sync on join/reconnect
//   • `prop`       — a delta (opened / hidden / shown) broadcast to everyone
//   • `interactFx` — a personal toast line ("Nothing fell…"), feedback only
// `read` (signposts) is the exception: pure client-side — the plaque text is
// derived deterministically from the sign's position (directions to landmarks),
// so there's nothing to validate or share.
//
// Rendering the state: a hidden (picked) scattered deco is suppressed via
// instanced.ts setDecoHidden; an opened chest is a real placed Group
// (world.userData.propGroups) whose children are swapped to the "<model>-open"
// coded build.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { getPropFocus, clearPropFocus, registerPropPicker, type PropHandle } from "./focus.ts";
import { pickInstanced, setDecoHidden, isDecoHidden } from "./instanced.ts";
import type { WorldInteractable } from "./world.ts";
import { buildDeco, interactReach, type DecoSpec } from "./deco/define.ts";
import { modelBuilder } from "./model/index.ts";
import { variantSeed } from "./model/variants.ts";
import { mapById } from "./content/load.ts";
import { ACTIVE_MAP_ID } from "./map/active.ts";
import { chatTyping } from "./chat.ts";
import { hasFreeSlot, flashToast, equippedTool } from "./inventory.ts";
import { startCooldown, onCooldown } from "./cooldown.ts";
import { startAttack } from "./player.ts";
import { enterCookMode } from "./cooking.ts";
import { DECOS } from "./deco/index.ts";
import { itemSpecOrNull } from "./items/index.ts";
import { isChopping } from "./items/define.ts";
import { sendInteract, sendChopStart } from "./net.ts";
import { tryDoorHotkey } from "./doors.ts";
import { navWorld, setPropColliderCut } from "./physics.ts";
import { reachDist } from "./shared.ts";
import type { ItemType } from "./shared.ts";

function hasHatchetEquipped(): boolean {
  const t = equippedTool();
  if (!t) return false;
  const s = itemSpecOrNull(t);
  return !!s && isChopping(s);
}

/** Shared cooldown key for the interact slot (drives its recharge sweep). */
export const INTERACT_CD = "player.interact";
const INTERACT_CD_S = 0.8; // client-side pacing; the server rate-checks too

const KIND_ICON: Record<string, string> = { open: "📦", shake: "✊", pick: "✋", read: "📜", use: "🍳", chop: "🪓" };

type Entry = WorldInteractable & { group?: THREE.Group; opened: boolean; cut: boolean; stump?: THREE.Group };

const props = new Map<string, Entry>();
/** Parent for stump overlays (set by setInteractWorld). */
let stumpRoot: THREE.Group | null = null;

let plaqueEl: HTMLElement | null = null;
let plaqueTitleEl: HTMLElement | null = null;
let plaqueBodyEl: HTMLElement | null = null;
let plaqueFor: Entry | null = null; // open plaque's sign (auto-closes when we stray)

function dist(p: Entry): number {
  const q = game.player.position;
  return reachDist(q.x, game.vert.y, q.z, p.x, p.y, p.z); // Infinity a floor apart (gravity plan)
}

/** Reach for a prop (the shared formula — see interactReach in deco/define.ts). */
function reach(p: Entry): number {
  return interactReach(p.spec.interact!, p.spec.radius, p.scale);
}

function handleOf(p: Entry): PropHandle {
  const it = p.spec.interact!;
  return {
    id: p.id, name: it.name, verb: it.verb,
    x: p.x, y: p.y, z: p.z,
    radius: p.spec.radius * p.scale,
  };
}

/** Raycast the interactable props: placed Groups directly, scattered decos via
 *  the instanced picker. Registered with focus.ts (which owns the click). */
function pick(rc: THREE.Raycaster): { prop: PropHandle; distance: number } | null {
  let best: { prop: PropHandle; distance: number } | null = null;
  for (const p of props.values()) {
    if (!p.group || !p.group.visible) continue;
    const hit = rc.intersectObject(p.group, true)[0];
    if (hit && (!best || hit.distance < best.distance)) {
      best = { prop: handleOf(p), distance: hit.distance };
    }
  }
  const inst = pickInstanced(rc);
  if (inst && (!best || inst.distance < best.distance)) {
    const p = props.get(inst.id);
    if (p) best = { prop: handleOf(p), distance: inst.distance };
  }
  return best;
}

/** Register the built world's interactable props. Split from initInteract
 *  (plan 21): the world builds at SPAWN time (the map arrives on the wire),
 *  long after the listener wiring below must have registered. */
export function setInteractWorld(world: THREE.Group): void {
  // Drop any leftover stump overlays from a prior map.
  for (const p of props.values()) {
    if (p.stump) { p.stump.parent?.remove(p.stump); p.stump = undefined; }
  }
  props.clear();
  stumpRoot = world;
  const groups = (world.userData.propGroups ?? new Map()) as Map<string, THREE.Group>;
  for (const it of (world.userData.interactables ?? []) as WorldInteractable[]) {
    props.set(it.id, { ...it, group: groups.get(it.id), opened: false, cut: false });
  }
}

/** Lookup for woodcutting (range checks). */
export function getInteractProp(id: string): Entry | undefined {
  return props.get(id);
}

export function isPropCut(id: string): boolean {
  return !!props.get(id)?.cut;
}

export function markPropCut(id: string, cut: boolean): void {
  const p = props.get(id);
  if (p) setCut(p, cut);
}

/** Wire up the interaction system. MUST run before initFocus: our keydown
 *  listener registers first so an open plaque eats Esc (stopImmediatePropagation)
 *  instead of also dropping focus, and the prop picker is in place before the
 *  first click. Props register later via setInteractWorld (world builds at spawn). */
export function initInteract(): void {
  document.getElementById("interactBtn")?.addEventListener("click", () => {
    if (getPropFocus()) doInteract();
    else tryDoorHotkey();
  });
  plaqueEl = document.getElementById("plaque");
  plaqueTitleEl = plaqueEl?.querySelector(".plaque-title") as HTMLElement | null;
  plaqueBodyEl = plaqueEl?.querySelector(".plaque-body") as HTMLElement | null;
  plaqueEl?.querySelector(".plaque-close")?.addEventListener("click", closePlaque);

  addEventListener("keydown", (e) => {
    if (e.key === "Escape" && plaqueFor) {
      closePlaque();
      e.stopImmediatePropagation(); // keep focus.ts from also dropping the focus
      return;
    }
    // F: prop Interact when a prop is focused; else structure door Open/Close
    // (doors yield while fighting — combat.ts owns F as the primary attack).
    if (e.key.toLowerCase() === "f" && !chatTyping()) {
      if (getPropFocus()) doInteract();
      else if (!game.fighting) tryDoorHotkey();
    }
  });

  registerPropPicker(pick);
}

/** Action-bar Interact slot state for combat.ts to render (it owns the bar). */
export function interactSlotState(): { show: boolean; enabled: boolean; label: string; icon: string } {
  const f = getPropFocus();
  const p = f ? props.get(f.id) : undefined;
  if (!p) return { show: false, enabled: false, label: "", icon: "" };
  const it = p.spec.interact!;
  if (it.kind === "open" && p.opened) {
    return { show: true, enabled: false, label: "Opened", icon: KIND_ICON.open! };
  }
  if (it.kind === "chop") {
    // Only offer Cut when a hatchet is equipped; cut stumps have nothing to do.
    if (p.cut || !hasHatchetEquipped()) {
      return { show: false, enabled: false, label: "", icon: "" };
    }
  }
  const enabled = !game.down && !game.talking && !onCooldown(INTERACT_CD) && dist(p) <= reach(p);
  return { show: true, enabled, label: it.verb, icon: KIND_ICON[it.kind] ?? "✋" };
}

/** Perform the focused prop's interaction (slot click / F). Client-side gates
 *  mirror the server's; `read` resolves entirely here (the plaque). */
export function doInteract(): void {
  if (inputLocked()) return; // another activity owns input (incl. aiming/cooking, previously missed)
  const f = getPropFocus();
  const p = f ? props.get(f.id) : undefined;
  if (!p) return;
  const it = p.spec.interact!;
  if (onCooldown(INTERACT_CD)) return;
  if (dist(p) > reach(p)) { flashToast("Too far away"); return; }

  if (it.kind === "read") { openPlaque(p); return; }
  if (it.kind === "use") {
    // A station verb (plan 25): fire the local activity keyed by the deco type
    // — the kitchen range opens cook mode. Client-resolved like `read`; the
    // activity's own intent (`cook`) is what the server validates.
    if (p.spec.type === "range") enterCookMode({ x: p.x, z: p.z, kind: "range" });
    return;
  }
  if (it.kind === "chop") {
    // Tree-cutting: client starts the session intent; server validates + locks.
    // (woodcutting.ts mounts the minigame on chopOn — no import cycle.)
    if (p.cut) { flashToast("This tree is already cut"); return; }
    if (!hasHatchetEquipped()) { flashToast("Equip a hatchet first"); return; }
    startCooldown(INTERACT_CD, INTERACT_CD_S);
    sendChopStart(p.id);
    return;
  }
  if (it.kind === "open" && p.opened) return;
  if (it.kind === "pick" && it.item && !hasFreeSlot(it.item as ItemType)) {
    flashToast("Inventory full");
    return;
  }

  startCooldown(INTERACT_CD, INTERACT_CD_S);
  startAttack(game.player, "punch"); // reach out so the action reads on every client
  sendInteract(p.id);
}

/** Per-frame: drop a prop focus whose instance the server hid (someone picked
 *  the flower), and close a plaque we've walked away from. */
export function updateInteract(): void {
  const f = getPropFocus();
  if (f && isDecoHidden(f.id)) clearPropFocus();
  if (plaqueFor && dist(plaqueFor) > reach(plaqueFor) + 2) closePlaque();
}

/** Nearest standing choppable tree to (x,z) — or the player if omitted.
 *  Used by the quest coach (`pointNearestChop`) so the tutorial arrow tracks a
 *  real live tree rather than a fixed map coordinate. Skips cut stumps. */
export function nearestChopTarget(x?: number, z?: number): { id: string; x: number; z: number } | null {
  const px = x ?? game.player?.position.x ?? 0;
  const pz = z ?? game.player?.position.z ?? 0;
  let best: { id: string; x: number; z: number; d2: number } | null = null;
  for (const p of props.values()) {
    if (p.spec.interact?.kind !== "chop") continue;
    if (p.cut) continue;
    if (!p.placed && isDecoHidden(p.id)) continue;
    const d2 = (p.x - px) * (p.x - px) + (p.z - pz) * (p.z - pz);
    if (!best || d2 < best.d2) best = { id: p.id, x: p.x, z: p.z, d2 };
  }
  return best ? { id: best.id, x: best.x, z: best.z } : null;
}

// ---- Server state appliers (called by net.ts) -------------------------------

/** Full opened/hidden/cut sync (join + every reconnect): reconcile EVERY prop, so a
 *  stale mirror from before the reconnect can't linger. */
export function applyPropSync(opened: string[], hidden: string[], cut: string[] = []): void {
  const openedSet = new Set(opened);
  const hiddenSet = new Set(hidden);
  const cutSet = new Set(cut);
  for (const p of props.values()) {
    if (p.spec.interact!.kind === "open") setOpened(p, openedSet.has(p.id));
    if (p.spec.interact!.kind === "chop") setCut(p, cutSet.has(p.id));
    else if (!p.placed) setDecoHidden(p.id, hiddenSet.has(p.id));
  }
  const f = getPropFocus();
  if (f && (isDecoHidden(f.id) || props.get(f.id)?.cut)) clearPropFocus();
}

/** One prop changed (broadcast to everyone). */
export function applyPropDelta(id: string, state: string): void {
  const p = props.get(id);
  if (!p) return;
  if (state === "opened") setOpened(p, true);
  else if (state === "hidden") {
    setDecoHidden(id, true);
    if (getPropFocus()?.id === id) clearPropFocus();
  } else if (state === "shown") {
    setDecoHidden(id, false);
    if (p.cut) setCut(p, false); // regrow tree
  } else if (state === "cut") {
    setCut(p, true);
    if (getPropFocus()?.id === id) clearPropFocus();
  }
}

/** Personal interaction feedback ("Nothing fell…"). */
export function interactFx(note: string | undefined): void {
  if (note) flashToast(note);
}

/** Swap a placed prop between its closed and "<model>-open" builds. The Group
 *  keeps its transform/collider; only the meshes are rebuilt. */
function setOpened(p: Entry, opened: boolean): void {
  if (p.opened === opened) return;
  p.opened = opened;
  const g = p.group;
  const model = p.spec.model;
  if (!g || !model) return; // scattered "open" props don't exist today
  const build = modelBuilder(opened ? `${model}-open` : model);
  const spec: DecoSpec = { ...p.spec, build: (ctx) => build(ctx, ctx.seed) };
  const fresh = buildDeco(spec, variantSeed(p.id)); // same seed world.ts built it with
  g.clear();
  g.add(...fresh.children);
}

/** Hide the full tree (instanced or placed) and show a stump overlay until regrow.
 *  Also shrinks the prop-tagged solid collider so players walk past the stump. */
function setCut(p: Entry, cut: boolean): void {
  if (p.cut === cut) return;
  p.cut = cut;
  if (!p.placed) setDecoHidden(p.id, cut);
  else if (p.group) p.group.visible = !cut;

  // Solid footprint: full tree radius ↔ stump radius (same object the NavGrid holds).
  setPropColliderCut(navWorld.colliders, p.id, cut, p.scale);

  if (cut) {
    if (p.stump || !stumpRoot) return;
    const stumpSpec = DECOS.find((d) => d.type === "stump");
    if (!stumpSpec) return;
    const g = buildDeco(stumpSpec, variantSeed(p.id));
    g.position.set(p.x, p.y, p.z);
    g.scale.setScalar(p.scale);
    stumpRoot.add(g);
    p.stump = g;
  } else if (p.stump) {
    p.stump.parent?.remove(p.stump);
    p.stump = undefined;
  }
}

// ---- Signpost plaque --------------------------------------------------------
// Sign text is DERIVED, not authored: signposts are randomly scattered, so each
// one computes directions to the nearest landmarks from its own position —
// deterministic, so every client reads the same sign. Landmarks come from the
// ACTIVE map's placed structures (keyed by map id — a map swap rebuilds it; the
// old cache read the DEFAULT map's structures on every map, a latent bug): the
// castle and chapel by name, houses clustered into "the Village" (near the
// castle) and "the Eastern Hamlet" (the far group). Maps without those specs
// just get an empty landmark list — signs degrade gracefully.

type Landmark = { name: string; x: number; z: number };
let landmarksCache: Landmark[] | null = null;
let landmarksCacheFor: string | null = null; // the map id the cache was built for

function landmarks(): Landmark[] {
  if (landmarksCache && landmarksCacheFor === ACTIVE_MAP_ID) return landmarksCache;
  landmarksCacheFor = ACTIVE_MAP_ID;
  const out: Landmark[] = [];
  const village: { x: number; z: number }[] = [];
  const hamlet: { x: number; z: number }[] = [];
  for (const s of mapById(ACTIVE_MAP_ID)?.structures ?? []) {
    if (s.spec === "castle") out.push({ name: "the Castle", x: s.x, z: s.z });
    else if (s.spec === "chapel") out.push({ name: "the Chapel", x: s.x, z: s.z });
    // Cottages are content docs now (standard-house / log-house) — not coded "house".
    else if (s.spec === "standard-house" || s.spec === "log-house") {
      (Math.hypot(s.x, s.z) < 150 ? village : hamlet).push(s);
    }
  }
  const centroid = (pts: { x: number; z: number }[], name: string) => {
    if (!pts.length) return;
    out.push({
      name,
      x: pts.reduce((a, p) => a + p.x, 0) / pts.length,
      z: pts.reduce((a, p) => a + p.z, 0) / pts.length,
    });
  };
  centroid(village, "the Village");
  centroid(hamlet, "the Eastern Hamlet");
  return (landmarksCache = out);
}

const OCTANTS = ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"];

/** Compass octant from (dx,dz), matching the world convention north = −z. */
function compass(dx: number, dz: number): string {
  const a = Math.atan2(dx, -dz); // 0 = north, +π/2 = east (+x)
  return OCTANTS[((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8]!;
}

function openPlaque(p: Entry): void {
  if (!plaqueEl) return;
  const lines = landmarks()
    .map((l) => ({ l, d: Math.hypot(l.x - p.x, l.z - p.z) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, 3)
    .map(({ l, d }) => `${l.name} — ${Math.max(1, Math.round(d))} paces ${compass(l.x - p.x, l.z - p.z)}`);
  if (plaqueTitleEl) plaqueTitleEl.textContent = p.spec.interact!.name;
  if (plaqueBodyEl) {
    plaqueBodyEl.replaceChildren(
      ...lines.map((t) => {
        const li = document.createElement("div");
        li.className = "plaque-line";
        li.textContent = t;
        return li;
      }),
    );
  }
  plaqueEl.style.display = "flex";
  plaqueFor = p;
}

function closePlaque(): void {
  if (plaqueEl) plaqueEl.style.display = "none";
  plaqueFor = null;
}
